import HiddenInput from 'components/Common/Form/Input/HiddenInput'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Divider from 'LuxKit/Divider'
import Caption from 'LuxKit/Typography/Caption'
import React, { FocusEventHandler, useCallback, useEffect } from 'react'
import RecentSearchSuggestions from '../SearchForm/SearchLocationInput/RecentSearchSuggestions'
import SearchMenuListItem from '../SearchForm/SearchMenu/SearchMenuListItem'
import SearchPlaceSelectList from './SearchPlaceSelectList'
import styled from 'styled-components'
import TextInputOnly from 'components/Common/Form/Input/TextInputOnly'
import IconButton from 'LuxKit/Button/IconButton'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import { LOCATION_SEARCH_ERROR_MESSAGE } from 'constants/search'
import { rem } from 'polished'
import { TypeaheadActionType } from 'analytics/snowplow/events'
import useSearchGridColumns from 'components/SearchV2/Hooks/useSearchGridColunms'

const PlaceControl = styled(VerticalSpacer)`
  position: relative;
  width: 100%;
`

const SearchItemContainer = styled.div`
  margin-top: ${rem(8)};
`

interface Props {
  className?: string;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  value?: App.SearchItem;
  required?: boolean;
  placeholder: string;
  localInputRef: React.RefObject<HTMLInputElement>;
  searchPhrase: string;
  onClearClick: () => void;
  finalOpen: boolean;
  showPlaceholder: boolean;
  hasRecentSearchItems: boolean;
  recentSearches: Array<App.FullRecentSearch>;
  selectedDropdown: number | undefined;
  onRecentSearchDelete: (item: App.FullRecentSearch) => void;
  onRecentSearchClick: (item: App.FullRecentSearch) => void;
  placeholderTitle?: string;
  onLocationSelect: (
    searchItem?: App.SearchItem,
    mouseEvent?: React.MouseEvent<HTMLElement>,
    actionType?: TypeaheadActionType,
    position?: App.SearchItemPosition
  ) => void;
  placeTypes?: Array<App.SearchPlaceType>;
  searchTypes?: Array<App.SearchType>;
  searchVertical?: App.SearchPlaceVertical;
  overrideSearchItems?: Array<App.SearchItem>;
  setSearchPhrase: React.Dispatch<React.SetStateAction<string>>;
  placeholderItems: Array<App.SearchItem>;
  placeholdersSearches: Array<App.SearchItem>;
  handleResultsChange?: (
    results: Array<App.SearchItem>,
    searchPhrase: string,
    numColumns?: number,
  ) => void;
  showHotelImagesLayout?: boolean;
}

function SearchPlaceSelectInline(props: Props) {
  const {
    className,
    onKeyDown,
    value,
    required,
    placeholder,
    localInputRef,
    searchPhrase,
    showPlaceholder,
    hasRecentSearchItems,
    recentSearches,
    selectedDropdown,
    onRecentSearchDelete,
    onRecentSearchClick,
    placeholderTitle,
    onLocationSelect,
    placeTypes,
    searchTypes,
    searchVertical,
    overrideSearchItems,
    setSearchPhrase,
    onClearClick,
    placeholderItems,
    placeholdersSearches,
    handleResultsChange,
  } = props

  const { containerRef, itemRef, numColumns } = useSearchGridColumns(placeholderItems)

  const handleOnClick = useCallback((searchItem?: App.SearchItem) => {
    if (!searchItem) {
      onLocationSelect(searchItem)
      return
    }

    const itemIndex = placeholderItems.findIndex(item => item.value === searchItem.value)
    if (itemIndex === -1 || numColumns === undefined) {
      onLocationSelect(searchItem)
      return
    }

    const x = itemIndex % numColumns
    const y = Math.floor(itemIndex / numColumns)
    const numRows = Math.ceil(placeholderItems.length / numColumns)

    onLocationSelect(searchItem, undefined, undefined, { x, y, numRows, numCols: numColumns })
  }, [numColumns, onLocationSelect, placeholderItems])

  useEffect(() => {
    localInputRef.current?.focus({ preventScroll: true })
  }, [localInputRef])

  const handleFocus = useCallback<FocusEventHandler<HTMLInputElement>>((event) => {
    if (event.currentTarget.value) {
      event.currentTarget.select()
    }
  }, [])

  return (<PlaceControl gap={12} className={className} onKeyDown={onKeyDown}>
    {!!required && <HiddenInput
      value={value ? 'filled' : undefined}
      required
      invalidMessage={LOCATION_SEARCH_ERROR_MESSAGE}
    />}
    <TextInputOnly
      noValidationSpacing
      placeholder={placeholder}
      onFocus={handleFocus}
      ref={localInputRef}
      defaultValue={value?.format.mainText}
      onChange={e => setSearchPhrase(e.currentTarget.value)}
      endIcon={searchPhrase.length > 0 ?
        <IconButton kind="tertiary" onClick={onClearClick}><LineTimesIcon /></IconButton> :
        <LineSearchIcon />}
    />
    {searchVertical === 'hotel' &&
      <>
        {showPlaceholder && <VerticalSpacer gap={12}>
          {hasRecentSearchItems && <RecentSearchSuggestions
            recentSearches={recentSearches}
            onDelete={onRecentSearchDelete}
            selectedDropdown={selectedDropdown}
            onRecentSearchClick={onRecentSearchClick}
            iconBackground
            horizontal={false}
          />}
          {placeholderTitle && <Caption variant="large" weight="bold">{placeholderTitle}</Caption>}
          <SearchItemContainer ref={containerRef}>
            {placeholdersSearches.map((item, index) => <SearchMenuListItem
              key={item.value}
              searchItem={item}
              onClick={handleOnClick}
              imageId={item.imageId}
              searchVertical={searchVertical}
              useImage
              ref={index === 0 ? itemRef : undefined}
            />)}
          </SearchItemContainer>
        </VerticalSpacer>}
        {!showPlaceholder && <SearchPlaceSelectList
          searchPhrase={searchPhrase}
          onResultsChange={handleResultsChange}
          onChange={onLocationSelect}
          placeTypes={placeTypes}
          searchTypes={searchTypes}
          vertical={searchVertical}
          overrideSearchItems={overrideSearchItems}
          useImage
        />}
      </>}
    {searchVertical !== 'hotel' &&
      <>
        {showPlaceholder && <VerticalSpacer gap={12}>
          {hasRecentSearchItems && <>
            <RecentSearchSuggestions
              recentSearches={recentSearches}
              onDelete={onRecentSearchDelete}
              selectedDropdown={selectedDropdown}
              onRecentSearchClick={onRecentSearchClick}
            />
            <Divider kind="primary" />
          </>}
          {placeholderTitle && <Caption variant="large" weight="bold">{placeholderTitle}</Caption>}
          <div ref={containerRef}>
            {placeholderItems.map((item, index) => <SearchMenuListItem
              key={item.value}
              searchItem={item}
              onClick={handleOnClick}
              searchVertical={searchVertical}
              useImage={searchVertical === 'tour'}
              ref={index === 0 ? itemRef : undefined}
            />)}
          </div>
        </VerticalSpacer>}
        {!showPlaceholder && <SearchPlaceSelectList
          searchPhrase={searchPhrase}
          onResultsChange={handleResultsChange}
          onChange={onLocationSelect}
          placeTypes={placeTypes}
          searchTypes={searchTypes}
          vertical={searchVertical}
          overrideSearchItems={overrideSearchItems}
          useImage={searchVertical === 'tour'}
        />}
      </>}
  </PlaceControl>
  )
}

export default SearchPlaceSelectInline
